import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  CheckCircleIcon,
  XCircleIcon,
  UsersIcon,
} from '@heroicons/react/24/outline'
import { TeamService } from '../../../shared/services/teamService'
import { useTranslation } from 'react-i18next'

const TeamJoinPage: React.FC = () => {
  const { t } = useTranslation()
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [joining, setJoining] = useState(false)
  const [result, setResult] = useState<{
    success: boolean
    message: string
    teamName?: string
  } | null>(null)

  useEffect(() => {
    if (!token) {
      setResult({
        success: false,
        message: t('teamJoin.invalidLink'),
      })
      setLoading(false)
      return
    }

    // 토큰 유효성 검증은 서버에서 처리
    setLoading(false)
  }, [token])

  const handleJoinTeam = async () => {
    if (!token) {
      return
    }

    setJoining(true)
    try {
      const response = await TeamService.joinTeam(token)

      if (response.success) {
        setResult({
          success: true,
          message: t('teamJoin.successBody'),
          teamName: response.team?.name,
        })

        // 3초 후 팀 협업 페이지로 이동
        setTimeout(() => {
          navigate('/team-collaboration')
        }, 3000)
      } else {
        setResult({
          success: false,
          message: response.message || t('teamJoin.joinError'),
        })
      }
    } catch (error) {
      console.error('팀 참여 실패:', error)
      setResult({
        success: false,
        message: t('teamJoin.networkError'),
      })
    } finally {
      setJoining(false)
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">초대 정보를 확인하는 중...</p>
        </div>
      </div>
    )
  }

  if (result) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md rounded-xl bg-white p-8 text-center shadow-lg">
          {result.success ? (
            <div>
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                <CheckCircleIcon className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="mb-2 text-xl font-semibold text-gray-900">
                {t('teamJoin.success')}
              </h2>
              <p className="mb-4 text-gray-600">{result.message}</p>
              {result.teamName && (
                <p className="mb-6 text-sm text-gray-500">
                  <strong>{result.teamName}</strong> 팀의 멤버가 되었습니다.
                </p>
              )}
              <div className="text-sm text-gray-500">
                {t('teamJoin.redirect')}
              </div>
            </div>
          ) : (
            <div>
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                <XCircleIcon className="h-8 w-8 text-red-600" />
              </div>
              <h2 className="mb-2 text-xl font-semibold text-gray-900">
                {t('teamJoin.failed')}
              </h2>
              <p className="mb-6 text-gray-600">{result.message}</p>
              <button
                onClick={() => navigate('/team-collaboration')}
                className="rounded-lg bg-blue-600 px-6 py-2 text-white transition-colors hover:bg-blue-700"
              >
                {t('teamJoin.goToTeam')}
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md rounded-xl bg-white p-8 text-center shadow-lg">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
          <UsersIcon className="h-8 w-8 text-blue-600" />
        </div>

        <h1 className="mb-2 text-2xl font-bold text-gray-900">팀 초대</h1>
        <p className="mb-8 text-gray-600">
          팀에 참여하시겠습니까? 초대를 수락하면 팀의 북마크와 리소스에 접근할
          수 있습니다.
        </p>

        <div className="space-y-3">
          <button
            onClick={handleJoinTeam}
            disabled={joining}
            className="w-full transform rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-3 text-white transition-all hover:scale-105 hover:from-blue-700 hover:to-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {joining ? (
              <div className="flex items-center justify-center">
                <div className="mr-2 h-5 w-5 animate-spin rounded-full border-b-2 border-white"></div>
                {t('teamJoin.joining')}
              </div>
            ) : (
              t('teamJoin.join')
            )}
          </button>

          <button
            onClick={() => navigate('/')}
            className="w-full rounded-lg bg-gray-100 px-6 py-3 text-gray-700 transition-colors hover:bg-gray-200"
          >
            {t('teamJoin.later')}
          </button>
        </div>

        <p className="mt-6 text-xs text-gray-500">{t('teamJoin.terms')}</p>
      </div>
    </div>
  )
}

export default TeamJoinPage
